import { Injectable, inject } from '@angular/core';
import { map, Observable } from 'rxjs';
import { Hub } from '../interfaces/hub.interface';
import { Subsidiary } from '../interfaces/subsidiary.interface';
import { environment } from '@environments/environment';
import { HttpClient } from '@angular/common/http';
import { Permission } from '@general-data/interfaces/permission.interface';
import { CancelReason } from '@general-data/interfaces/cancel-reason.interface';
import { AmbigousScanResponse, GenericScanResp } from '@core/interfaces/scan-response.interface';
import { Country } from '@general-data/interfaces/country.interface';
import { Checklist } from '@general-data/interfaces/checklist.interface';
import { CancelReasonByOrder } from '@general-data/interfaces/cancel-reason-by-order.interface';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  private http = inject(HttpClient);

  public getHubs(): Observable<Hub[]> {
    return this.http
      .get<Hub[]>(`${environment.api}/services/cerebro/hubs`)
      .pipe(map((hubs) => hubs.sort((a, b) => (a.country > b.country ? -1 : 1))));
  }

  public getSubsidiaries(): Observable<Subsidiary[]> {
    return this.http
      .get<{ subsidiaries: Subsidiary[] }>(`${environment.api}/services/meta/dictionaries?type=subsidiaries`)
      .pipe(map((resp) => resp.subsidiaries));
  }

  public getVehicleTypes(): Observable<string[]> {
    return this.http
      .get<{ vehicleTypes: string[] }>(`${environment.api}/services/cerebro/meta?types=vehicleTypes`)
      .pipe(map((response) => response.vehicleTypes));
  }

  public getPermissions(): Observable<Permission[]> {
    return this.http.get<Permission[]>(`${environment.api}/services/cerebro/management/permissions`);
  }

  public getCancelReasons(): Observable<CancelReason[]> {
    return this.http
      .get<{ cancelReason: CancelReason[] }>(`${environment.api}/services/cerebro/meta?types=cancelReason`)
      .pipe(
        map((response: { cancelReason: CancelReason[] }) =>
          Object.values(response.cancelReason).sort((a, b) => a.name.localeCompare(b.name))
        )
      );
  }

  public getCancelReasonsByOrders(ordersIds: string[]): Observable<CancelReasonByOrder[]> {
    return this.http.post<CancelReasonByOrder[]>(`${environment.api}/services/cerebro/orders/bulk/cancel-reasons`, {
      ordersIds,
    });
  }

  public getOrderTypes(): Observable<string[]> {
    return this.http
      .get<{ type: string[] }>(`${environment.api}/services/cerebro/meta?types=type`)
      .pipe(map((response) => response.type));
  }

  public getOrderCategories(): Observable<string[]> {
    return this.http
      .get<{ category: string[] }>(`${environment.api}/services/cerebro/meta?types=category`)
      .pipe(map((response) => response.category));
  }

  public searchOrder(
    trackingNumber: string,
    ambiguousSelected?: boolean
  ): Observable<GenericScanResp | AmbigousScanResponse> {
    if (ambiguousSelected) {
      return this.http.get<GenericScanResp | AmbigousScanResponse>(
        `${environment.api}/services/cerebro/orders/search?identifier=${trackingNumber}&allowAmbiguity=true&searchAttribute=trackingNumber`
      );
    }
    return this.http.get<GenericScanResp | AmbigousScanResponse>(
      `${environment.api}/services/cerebro/orders/search?identifier=${trackingNumber}&allowAmbiguity=true`
    );
  }

  public getCountries(): Observable<Country[]> {
    return this.http
      .get<{ countries: { [key: string]: Country } }>(`${environment.api}/services/meta/dictionaries?type=countries`)
      .pipe(map((resp: { countries: { [key: string]: Country } }) => Object.values(resp.countries)));
  }

  public getCustomerActionReasons(): Observable<string[]> {
    return this.http
      .get<{
        customerActionReasons: string[];
      }>(`${environment.api}/services/meta/dictionaries?type=customerActionReasons`)
      .pipe(map((resp: { customerActionReasons: string[] }) => resp.customerActionReasons));
  }

  public printAwbs(template: string, copies: number, trackingNumbers: string[]): Observable<string> {
    return this.http.post<string>(`${environment.api}/services/awb/`, {
      template,
      copies,
      objectIds: trackingNumbers,
      idAttribute: 'trackingNumber',
    });
  }

  public getChecklists(): Observable<Checklist[]> {
    return this.http.get<{ checkList: Checklist[] }>(`${environment.api}/services/cerebro/meta?types=checkList`).pipe(
      map((response) => {
        return response.checkList.sort((a, b) => {
          if (a.routeType === 'pickUp' && b.routeType === 'dropOff') {
            return -1;
          } else if (a.routeType === 'dropOff' && b.routeType === 'pickUp') {
            return 1;
          } else {
            return a.name.localeCompare(b.name);
          }
        });
      })
    );
  }

  public getStoryStatuses(): Observable<string[]> {
    return this.http
      .get<{ storyStatuses: string[] }>(`${environment.api}/services/cerebro/meta?types=storyStatuses`)
      .pipe(map((response) => response.storyStatuses.sort()));
  }

  public getRouteStatuses(): Observable<string[]> {
    return this.http
      .get<{ routeStatuses: string[] }>(`${environment.api}/services/cerebro/meta?types=routeStatuses`)
      .pipe(map((response) => response.routeStatuses.sort()));
  }

  public getStatusGroups(): Observable<string[]> {
    return this.http
      .get<{ statusGroups: string[] }>(`${environment.api}/services/cerebro/meta?types=statusGroups`)
      .pipe(map((response) => response.statusGroups.sort()));
  }

  public getContractPackages(): Observable<string[]> {
    return this.http
      .get<{ contractPackages: string[] }>(`${environment.api}/services/cerebro/meta?types=contractPackages`)
      .pipe(map((response) => response.contractPackages.sort()));
  }

  public getAppTypes(): Observable<string[]> {
    return this.http
      .get<{ appTypes: string[] }>(`${environment.api}/services/cerebro/meta?types=appTypes`)
      .pipe(map((response) => response.appTypes.sort()));
  }

  public getCallLogFieldsValues(): Observable<{
    communicationTypes: string[];
    departments: string[];
    interactionBy: string[];
    shipaEntities: string[];
    socialMediaPlatforms: string[];
    reasons: string[];
    reasonsByDepartment: { [department: string]: string[] };
  }> {
    return this.http
      .get<{
        callLogs: {
          communicationType: string[];
          department: string[];
          interactionBy: string[];
          shipaEntity: string[];
          socialMediaPlatform: string[];
          reason: string[];
          reasonsByDepartment: { [department: string]: string[] };
        };
      }>(`${environment.api}/services/cerebro/meta?types=callLogs`)
      .pipe(
        map((response) => ({
          communicationTypes: response.callLogs.communicationType.sort(),
          departments: response.callLogs.department.sort(),
          interactionBy: response.callLogs.interactionBy.sort(),
          shipaEntities: response.callLogs.shipaEntity.sort(),
          socialMediaPlatforms: response.callLogs.socialMediaPlatform.sort(),
          reasons: response.callLogs.reason.sort(),
          reasonsByDepartment: response.callLogs.reasonsByDepartment,
        }))
      );
  }

  public getBillingServiceTypes(): Observable<{
    dateFormats: string[];
    serviceTypes: string[];
    transactionDateStrategies: string[];
  }> {
    return this.http
      .get<{
        billing: {
          dateFormats: string[];
          serviceTypes: string[];
          transactionDateStrategies: string[];
        };
      }>(`${environment.api}/services/cerebro/meta?types=billing`)
      .pipe(
        map((response) => ({
          dateFormats: response.billing.dateFormats.sort(),
          serviceTypes: response.billing.serviceTypes.sort(),
          transactionDateStrategies: response.billing.transactionDateStrategies.sort(),
        }))
      );
  }
}
